import { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import { Flag, MessageCircle, BookOpen, Zap, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { ProfileSkeleton } from "@/components/teacher/profile/ProfileSkeleton";
import { ProfileHero } from "@/components/teacher/profile/ProfileHero";
import { AboutSection } from "@/components/teacher/profile/AboutSection";
import { IntroVideo } from "@/components/teacher/profile/IntroVideo";
import { SubjectExpertise } from "@/components/teacher/profile/SubjectExpertise";
import { TeachingDetails } from "@/components/teacher/profile/TeachingDetails";
import { OfferingsList } from "@/components/teacher/profile/OfferingsList";
import { ExamSuccessStories } from "@/components/teacher/profile/ExamSuccessStories";
import { TeacherReviewsSection } from "@/components/teacher/profile/TeacherReviewsSection";
import { CredentialsSection } from "@/components/teacher/profile/CredentialsSection";
import { ShareProfile } from "@/components/teacher/profile/ShareProfile";
import { ContactCard } from "@/components/teacher/profile/BookingCard";
import { InquiryComposerModal } from "@/components/InquiryComposerModal";
import { SaveTeacherButton } from "@/components/match/SaveTeacherButton";
import { savePendingIntent } from "@/utils/pendingIntent";
import { useAuth } from "@/hooks/use-auth-compat";
import { useMyInquiries } from "@/hooks/use-inquiries";
import { OPEN_THREAD_STATUSES } from "@/services/inquiryService";
import { getProfile } from "@/services/UserManagmentServices";
import {
  getTeacherByHandle,
  getTeacherOfferings,
  type PublicTeacherProfile,
  type PublicOffering,
} from "@/services/marketplaceService";
import { currencySymbol } from "@/lib/currency";

function computeMinPrice(offerings: PublicOffering[]): { minPrice: number; currency: string } | null {
  // No currency is favored here — picks the true minimum across whatever
  // currencies this teacher has configured. See ADR 067 / currency audit:
  // this previously restricted to INR-only prices whenever any existed,
  // which silently hid a teacher's lower USD/CAD price from the viewer.
  // The header labels this "from $X/session", so use the per-session figure
  // (perSessionEquivalent) — for GROUP/CRASH regularPrice is the course total,
  // not a per-session price. Prices come from server-currency-filtered
  // pricingPlans (ADR 067) in whole currency units.
  const allPrices: { price: number; currency: string }[] = offerings.flatMap(
    o => (o.pricingPlans ?? []).map(p => ({ price: p.perSessionEquivalent ?? p.regularPrice, currency: p.currency }))
  );
  if (allPrices.length === 0) return null;
  const min = allPrices.reduce((a, b) => (b.price < a.price ? b : a));
  return { minPrice: min.price, currency: min.currency };
}

export default function TeacherProfilePage() {
  const { handle } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  // Repeat-contact prevention: find an existing open thread with this teacher.
  const { data: myInquiries } = useMyInquiries(0, 50);
  const [profile, setProfile] = useState<PublicTeacherProfile | null>(null);
  const [offerings, setOfferings] = useState<PublicOffering[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [offeringsError, setOfferingsError] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [selectedOfferingId, setSelectedOfferingId] = useState<string | undefined>(undefined);
  const [goalOnboardingCompleted, setGoalOnboardingCompleted] = useState<boolean | null>(null);

  const existingThreadId = profile?.teacherUserId
    ? (myInquiries?.content ?? []).find((t) => t.teacherUserId === profile.teacherUserId && OPEN_THREAD_STATUSES.has(t.status))?.id
    : undefined;

  // Fetch user profile if authenticated (to get goalOnboardingCompleted)
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    (async () => {
      const token = await auth.getToken();
      if (!token) return;
      getProfile(token)
        .then(p => setGoalOnboardingCompleted(p.goalOnboardingCompleted))
        .catch(() => setGoalOnboardingCompleted(null));
    })();
  }, [auth.isAuthenticated, auth.getToken]);

  // Auto-open inquiry modal if navigated back from goal onboarding
  useEffect(() => {
    if ((location.state as { openInquiry?: boolean } | null)?.openInquiry) {
      setInquiryOpen(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (!handle) return;
    setLoading(true);
    setNotFound(false);
    setOfferingsError(false);

    Promise.allSettled([
      getTeacherByHandle(handle),
      getTeacherOfferings(handle),
    ]).then(([profileResult, offeringsResult]) => {
      if (profileResult.status === "rejected" || !profileResult.value) {
        setNotFound(true);
      } else {
        setProfile(profileResult.value);
      }
      if (offeringsResult.status === "rejected") {
        setOfferingsError(true);
      } else {
        setOfferings(offeringsResult.value);
      }
    }).finally(() => setLoading(false));
  }, [handle]);

  const handleContact = (offeringId?: string) => {
    if (!auth.isAuthenticated) {
      const raw = sessionStorage.getItem("bmc:search-context");
      const searchCtx = raw ? JSON.parse(raw) : null;
      localStorage.setItem("bmc:anonymous-search-context", JSON.stringify({
        teacherHandle: handle,
        intendedOfferingId: offeringId ?? null,
        searchQuery: searchCtx?.searchQuery ?? null,
        activeFilters: searchCtx?.activeFilters ?? null,
      }));
      window.dispatchEvent(new CustomEvent("bmc:open-registration-gate", { detail: { source: "teacher-profile" } }));
      return;
    }
    if (goalOnboardingCompleted === false) {
      if (handle) savePendingIntent({ type: 'INQUIRY', teacherHandle: handle });
      navigate('/goal-onboarding');
      return;
    }
    // goalOnboardingCompleted === true OR null (unknown — open modal optimistically)
    setSelectedOfferingId(offeringId);
    setInquiryOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Loading... | BookMyCoaching</title>
          <meta name="description" content="BookMyCoaching — find verified tutors and coaches across India." />
        </Helmet>
        <Header />
        <main className="pt-28">
          <ProfileSkeleton />
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Teacher Not Found | BookMyCoaching</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Header />
        <main className="pt-28 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-lg font-medium text-foreground mb-2">Teacher not found</p>
            <p className="text-sm text-muted-foreground mb-6">
              This profile may not exist or is not publicly available.
            </p>
            <Link
              to="/search"
              className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Back to search
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const priceInfo = computeMinPrice(offerings);
  const trialAvailable = offerings.some(o => o.trialSessionAvailable);

  const primarySubject = profile.subjects?.[0]?.subjectName ?? 'Tutor';
  const ogImage = profile.introVideo?.thumbnailUrl ?? profile.profileImage ?? getDefaultOgImage();
  const profileDescription = profile.professionalBio
    ? profile.professionalBio.slice(0, 155)
    : `${profile.fullName} offers coaching on BookMyCoaching.`;
  const profileTitle = `${profile.fullName} — ${primarySubject} | BookMyCoaching`;
  const profileCanonicalUrl = getCanonicalUrl(`/t/${profile.handle}`);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{profileTitle}</title>
        <meta name="description" content={profileDescription} />
        <link rel="canonical" href={profileCanonicalUrl} />
        <meta property="og:title" content={profileTitle} />
        <meta property="og:description" content={profileDescription} />
        <meta property="og:url" content={profileCanonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="profile" />
      </Helmet>
      <Header />

      <main className="pt-28 pb-24 lg:pb-0">

        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <ErrorBoundary>

          {/* Back */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => history.back()}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>

          {/* Hero */}
          <ProfileHero
            fullName={profile.fullName}
            handle={profile.handle}
            teacherTier={profile.teacherTier}
            profileImage={profile.profileImage}
            subjects={profile.subjects ?? []}
            locationDisplayName={profile.locationDisplayName}
            city={profile.city}
            country={profile.country}
            yearsTeachingExperience={profile.yearsTeachingExperience}
            highestQualification={profile.highestQualification}
            overallStatus={profile.overallStatus}
            offeringsCount={offerings.length}
            minPrice={priceInfo?.minPrice}
            minPriceCurrency={priceInfo?.currency}
            teachingModes={profile.teachingModes ?? []}
            instructionLanguages={profile.instructionLanguages ?? []}
          />



          {/* Main grid */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10">

            {/* ── Left column ── */}
            <div className="space-y-10 min-w-0">

              <AboutSection
                professionalBio={profile.professionalBio}
              />

              <IntroVideo introVideo={profile.introVideo} />

              <SubjectExpertise subjectExpertise={profile.subjectExpertise ?? []} />

              <TeachingDetails
                teachingAudience={profile.teachingAudience ?? []}
                curriculumSystems={profile.curriculumSystems ?? []}
                instructionLanguages={profile.instructionLanguages ?? []}
                otherInstructionLanguage={profile.otherInstructionLanguage}
                teachingModes={profile.teachingModes ?? []}
                locationDisplayName={profile.locationDisplayName}
                city={profile.city}
                state={profile.state}
                country={profile.country}
              />

              {/* Offerings */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Offerings</h2>
                {offeringsError ? (
                  <p className="text-sm text-muted-foreground py-4">
                    Could not load offerings. Please try again later.
                  </p>
                ) : (
                  <OfferingsList
                    offerings={offerings}
                    teacherHandle={profile.handle}
                    onContactOffering={handleContact}
                  />
                )}
              </section>

              <ExamSuccessStories examSuccessStories={profile.examSuccessStories ?? []} />

              <TeacherReviewsSection
                teacherId={profile.handle}
                teacherUserId={profile.teacherUserId ?? ""}
                teacherName={profile.fullName}
              />

              <CredentialsSection
                highestQualification={profile.highestQualification}
                highestQualificationInstitution={profile.highestQualificationInstitution}
                teachingQualification={profile.teachingQualification}
                yearsTeachingExperience={profile.yearsTeachingExperience}
                formerSchoolTeacher={profile.formerSchoolTeacher}
                formerInstitutionName={profile.formerInstitutionName}
                formerInstitutionYears={profile.formerInstitutionYears}
              />

              {/* Share & Report */}
              <div className="flex items-center gap-3 pb-8">
                <ShareProfile teacherName={profile.fullName} teacherHandle={profile.handle} />
                <button
                  className="p-2 text-muted-foreground hover:text-destructive transition-colors duration-150 rounded-lg hover:bg-muted cursor-pointer"
                  aria-label="Report profile"
                >
                  <Flag className="w-4 h-4" />
                </button>
              </div>

            </div>

            {/* ── Right sidebar — single consolidated card ── */}
            <div className="lg:sticky lg:top-20 self-start">
              <div className="rounded-2xl border border-[#DCDDEE] bg-[#F7F7FC] dark:bg-slate-800/50 dark:border-slate-700 shadow-sm overflow-hidden">

                {/* Price header band */}
                {(priceInfo?.minPrice != null || trialAvailable) && (
                  <div className="bg-gradient-to-br from-[hsl(237,52%,40%)] to-[hsl(237,52%,25%)] px-5 py-4">
                    {priceInfo?.minPrice != null && (
                      <div className="flex items-baseline gap-1">
                        <span className="text-white/70 text-sm font-medium">From</span>
                        <span className="text-white text-3xl font-extrabold leading-none">
                          {currencySymbol(priceInfo.currency)}{Math.round(priceInfo.minPrice)}
                        </span>
                        <span className="text-white/70 text-sm font-medium">/session</span>
                      </div>
                    )}
                    {trialAvailable && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <Zap className="w-3.5 h-3.5 text-amber-300" />
                        <span className="text-amber-200 text-xs font-semibold">Free trial session available</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Contact CTA */}
                <div className="px-5 py-4 border-b border-[#DCDDEE] dark:border-slate-700">
                  <ContactCard
                    fullName={profile.fullName}
                    handle={profile.handle}
                    trialAvailable={trialAvailable}
                    offeringsCount={offerings.length}
                    minPrice={priceInfo?.minPrice}
                    minPriceCurrency={priceInfo?.currency}
                    onAuthenticatedContact={handleContact}
                    existingThreadId={existingThreadId}
                  />
                </div>

                {/* Save teacher */}
                {auth.isAuthenticated && profile.handle && (
                  <div className="px-5 py-4 border-b border-[#DCDDEE] dark:border-slate-700">
                    <SaveTeacherButton handle={profile.handle} name={profile.fullName} className="w-full" />
                  </div>
                )}

                {/* Subjects */}
                {(profile.subjects ?? []).length > 0 && (
                  <div className="px-5 py-4 border-b border-[#DCDDEE] dark:border-slate-700">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#5A5E80] mb-3">Subjects</p>
                    <div className="flex flex-wrap gap-2">
                      {(profile.subjects ?? []).map(s => (
                        <span
                          key={s.subjectId}
                          className="inline-flex items-center gap-1.5 bg-[hsl(237,52%,31%)]/8 text-[hsl(237,52%,31%)] rounded-full px-3 py-1 text-xs font-semibold"
                        >
                          <BookOpen className="h-3 w-3 shrink-0" />
                          {s.subjectName}
                        </span>
                      ))}
                    </div>
                    {(profile.subjects ?? []).some(s => s.topicNames?.length > 0) && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(profile.subjects ?? []).flatMap(s => s.topicNames ?? []).slice(0, 8).map((t, i) => (
                          <span key={i} className="text-xs bg-[hsl(237,52%,31%)]/5 text-[hsl(237,52%,40%)] rounded-full px-2.5 py-0.5 font-medium">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Teaching Approach */}
                {profile.teachingApproach && (() => {
                  const chips = profile.teachingApproach.split(",").map((s: string) => s.trim()).filter(Boolean);
                  if (chips.length === 0) return null;
                  return (
                    <div className="px-5 py-4">
                      <p className="text-xs font-bold uppercase tracking-widest text-[#5A5E80] mb-3">Teaching Approach</p>
                      <div className="flex flex-wrap gap-2">
                        {chips.map((chip: string) => (
                          <span
                            key={chip}
                            className="text-xs font-medium px-3 py-1 rounded-full bg-[hsl(237,52%,31%)]/8 text-[hsl(237,52%,31%)]"
                          >
                            {chip}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

              </div>
            </div>

          </div>
        </ErrorBoundary>
        </div>
      </main>

      {/* Sticky mobile enquire bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-t border-border px-4 py-3 flex items-center justify-between gap-3 shadow-lg">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{profile.fullName}</p>
          {priceInfo && (
            <p className="text-xs text-muted-foreground">
              from {currencySymbol(priceInfo.currency)}{Math.round(priceInfo.minPrice).toLocaleString()}/session
            </p>
          )}
        </div>
        <Button
          size="sm"
          className="gap-2 flex-shrink-0"
          onClick={() => handleContact()}
        >
          <MessageCircle className="w-4 h-4" />
          Enquire
        </Button>
      </div>

      {profile?.teacherUserId && (
        <InquiryComposerModal
          teacherUserId={profile.teacherUserId}
          teacherName={profile.fullName}
          teacherHandle={profile.handle}
          offeringId={selectedOfferingId}
          open={inquiryOpen}
          onClose={() => { setInquiryOpen(false); setSelectedOfferingId(undefined); }}
        />
      )}

      <Footer />
    </div>
  );
}
